/**
 * 登录页（架构 §2 src/pages/LoginPage.tsx / T10）
 *  - POST /auth/login {username,password} -> {token,user}
 *  - 1003 AUTH_WRONG_PASSWORD -> 红字提示
 *  - 两位创始人用 seed 内置账号直接登录（无需邀请码）
 */
import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Paper from '@mui/material/Paper';
import { login } from '../api/auth.api';
import { ApiError } from '../api/client';
import { useAuthStore } from '../store/auth.store';
import { ErrorCode } from '../types/models';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = (location.state as { from?: string } | null)?.from || '/';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!username || !password) {
      setError('请输入账号和密码');
      return;
    }
    setLoading(true);
    try {
      const res = await login(username.trim(), password);
      setAuth(res.token, res.user);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === ErrorCode.AUTH_WRONG_PASSWORD) {
        setError('账号或密码错误');
      } else if (err instanceof ApiError) {
        setError(err.message || '登录失败');
      } else {
        setError('网络错误，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper sx={{ p: 4, width: 360 }} elevation={0} variant="outlined">
        <Typography variant="h5" align="center" color="primary.main" gutterBottom>
          知会 ZhiHui
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          登录你的账号
        </Typography>
        <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="账号"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            fullWidth
            autoComplete="username"
          />
          <TextField
            label="密码"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? '\u9690\u85cf\u5bc6\u7801' : '\u663e\u793a\u5bc6\u7801'}
                    edge="end"
                    onClick={() => setShowPassword((visible) => !visible)}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained" disabled={loading} size="large">
            {loading ? '登录中…' : '登录'}
          </Button>
        </Box>
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          还没有账号？<Link to="/register">用邀请码注册</Link>
        </Typography>
      </Paper>
    </Box>
  );
}
